import React from 'react';
import dayjs from 'dayjs';
import { cn } from '@frontend/shadcn/lib/utils';
import { PercentChangeBadge } from './percent-change-badge';
import { DateType } from '../types';

interface MetricChangeIndicatorProps {
  current: number;
  previous: number;
  startDate?: string;
  endDate?: string;
  dateType?: DateType;
  className?: string;
  showTimeRange?: boolean;
  timeRangePrefix?: string;
  customTimeRangeText?: string;
}

// Helper function to calculate percentage change
const calculatePercentageChange = (current: number, previous: number): number => {
  if (previous === 0) {
    return current > 0 ? 100 : 0;
  }
  return ((current - previous) / previous) * 100;
};

// Helper function to get friendly date text for time ranges
const getFriendlyDateText = (startDate?: string, endDate?: string, dateType?: DateType): string => {
  // Handle predefined date types with friendly names
  if (dateType) {
    switch (dateType) {
      case 'thisWeek':
        return 'this week';
      case 'lastWeek':
        return 'last week';
      case 'monthToDate':
        return 'month to date';
      case 'lastMonth':
        return 'last month';
      case 'quarterToDate':
        return 'quarter to date';
      case 'lastQuarter':
        return 'last quarter';
      case 'last6Months':
        return 'last 6 months';
      case 'yearToDate':
        return 'year to date';
      case 'allTime':
        return 'all time';
      default:
        break;
    }
  }

  // For custom ranges or when dateType is not provided, display the actual date range
  if (!startDate || !endDate) {
    return 'selected period';
  }

  const start = dayjs(startDate);
  const end = dayjs(endDate);

  // Check if it's the same day
  if (start.isSame(end, 'day')) {
    return start.format('MMM D, YYYY');
  }

  // Check if it's the same year
  if (start.year() === end.year()) {
    return `${start.format('MMM D')} - ${end.format('MMM D, YYYY')}`;
  }

  // Different years
  return `${start.format('MMM D, YYYY')} - ${end.format('MMM D, YYYY')}`;
};

export const MetricChangeIndicator: React.FC<MetricChangeIndicatorProps> = ({
  current,
  previous,
  startDate,
  endDate,
  dateType,
  className = '',
  showTimeRange = true,
  timeRangePrefix = 'in',
  customTimeRangeText,
}) => {
  // Calculate percentage change
  const growthPercentage = calculatePercentageChange(current, previous);

  // Get friendly date text if showing time range
  const timeRange = showTimeRange ? customTimeRangeText || getFriendlyDateText(startDate, endDate, dateType) : '';

  return (
    <div className={cn('text-sm text-muted-foreground flex items-center gap-2', className)}>
      <PercentChangeBadge percent={Math.round(growthPercentage)} />
      {showTimeRange && timeRange && (
        <span className="font-sans text-sm font-normal leading-6 text-gray-500">
          {timeRangePrefix} {timeRange}
        </span>
      )}
    </div>
  );
};

// Convenience component for just the percentage badge without time range
export const MetricPercentageBadge: React.FC<Pick<MetricChangeIndicatorProps, 'current' | 'previous'>> = ({
  current,
  previous,
}) => {
  const growthPercentage = calculatePercentageChange(current, previous);
  return <PercentChangeBadge percent={Math.round(growthPercentage)} />;
};

export default MetricChangeIndicator;
